import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { SetDetail } from 'store/services/trade/tradeApi';

export interface SidebarTradeCard {
  id: string;                    // Unique line item ID
  wantListId: number;            // Want list entry reference
  cardId?: number;               // Specific printing ID
  cardName: string;
  set: string;
  setCode: string;
  condition: string;
  language: string;
  acceptableLanguages?: string[];  // Backend lowercase codes from want list (e.g. ['es'])
  foil: boolean;
  imageUrl: string;
  pointValue: number;
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  scryfallId?: string;
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
  isInvalid?: boolean;           // Error state flag
  maxQuantity: number;           // Original quantity from want list
  languageMultiplierApplied?: boolean;
  multiplierValue?: number;
}

interface ProfileTradeSidebarContextType {
  // State
  cards: SidebarTradeCard[];
  isOpen: boolean;
  receiverUsername: string | null;
  receiverId: number | null;

  // Computed
  totalCards: number;
  totalPoints: number;

  // Actions
  addCard: (card: Omit<SidebarTradeCard, 'id'>) => void;
  removeCard: (id: string) => void;
  updateCard: (id: string, updates: Partial<SidebarTradeCard>) => void;
  clearAll: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  setReceiver: (username: string, id: number) => void;
  getAddedCountForWantListId: (wantListId: number) => number;
}

const ProfileTradeSidebarContext = createContext<ProfileTradeSidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'profile_trade_sidebar';

interface StorageData {
  cards: SidebarTradeCard[];
  receiverUsername: string | null;
  receiverId: number | null;
}

export const ProfileTradeSidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cards, setCards] = useState<SidebarTradeCard[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [receiverUsername, setReceiverUsername] = useState<string | null>(null);
  const [receiverId, setReceiverId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from session storage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: StorageData = JSON.parse(stored);
        setCards(data.cards || []);
        setReceiverUsername(data.receiverUsername);
        setReceiverId(data.receiverId);
        // Open sidebar if there are cards
        if (data.cards && data.cards.length > 0) {
          setIsOpen(true);
        }
      }
    } catch (e) {
      console.error('Failed to load trade sidebar from storage:', e);
    }
    setIsInitialized(true);
  }, []);

  // Save to session storage when state changes
  useEffect(() => {
    if (!isInitialized) return;

    try {
      const data: StorageData = {
        cards,
        receiverUsername,
        receiverId,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save trade sidebar to storage:', e);
    }
  }, [cards, receiverUsername, receiverId, isInitialized]);

  const totalCards = useMemo(() => cards.length, [cards]);
  const totalPoints = useMemo(() =>
    cards.reduce((sum, card) => sum + card.pointValue, 0),
    [cards]
  );

  const addCard = useCallback((card: Omit<SidebarTradeCard, 'id'>) => {
    const id = `${card.wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCards(prev => [...prev, { ...card, id }]);
    setIsOpen(true);
  }, []);

  const removeCard = useCallback((id: string) => {
    setCards(prev => prev.filter(card => card.id !== id));
  }, []);

  const updateCard = useCallback((id: string, updates: Partial<SidebarTradeCard>) => {
    setCards(prev => prev.map(card =>
      card.id === id ? { ...card, ...updates } : card
    ));
  }, []);

  const clearAll = useCallback(() => {
    setCards([]);
    setIsOpen(false);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const openSidebar = useCallback(() => setIsOpen(true), []);
  const closeSidebar = useCallback(() => setIsOpen(false), []);

  const setReceiver = useCallback((username: string, id: number) => {
    // If switching to a different user, clear the cart
    if (receiverId !== null && receiverId !== id) {
      setCards([]);
    }
    setReceiverUsername(username);
    setReceiverId(id);
  }, [receiverId]);

  const getAddedCountForWantListId = useCallback((wantListId: number) => {
    return cards.filter(card => card.wantListId === wantListId).length;
  }, [cards]);

  const value: ProfileTradeSidebarContextType = {
    cards,
    isOpen,
    receiverUsername,
    receiverId,
    totalCards,
    totalPoints,
    addCard,
    removeCard,
    updateCard,
    clearAll,
    openSidebar,
    closeSidebar,
    setReceiver,
    getAddedCountForWantListId,
  };

  return (
    <ProfileTradeSidebarContext.Provider value={value}>
      {children}
    </ProfileTradeSidebarContext.Provider>
  );
};

export const useProfileTradeSidebar = (): ProfileTradeSidebarContextType => {
  const context = useContext(ProfileTradeSidebarContext);
  if (!context) {
    throw new Error('useProfileTradeSidebar must be used within a ProfileTradeSidebarProvider');
  }
  return context;
};

export default ProfileTradeSidebarContext;
